import { useState } from 'react';
import { Button, IconButton } from '@mui/material';

import { Icon } from '../../../shared/Icon';
import {
    HexColor,
    hexToUnityColor,
    UnityColor,
    unityColorToHex,
} from '@lifeserver/shared';

import styles from './GradientInput.module.scss';

export interface GradientInputProps {
    value?: UnityColor[];
    onChange?: (value: UnityColor[]) => void;
    minColors?: number;
    maxColors?: number;
}

export const GradientInput = ({
    value = [],
    onChange,
    minColors = 2,
    maxColors = 11,
}: GradientInputProps) => {
    const [selectedColorIndex, setSelectedColorIndex] = useState<number>(0);

    const colors =
        value.length >= minColors ? value : getDefaultColors(minColors);

    // Calculate uniform positions for each color
    const getPosition = (index: number) => {
        if (colors.length === 1) return 0.5;
        return index / (colors.length - 1);
    };

    const handleAddColor = () => {
        if (colors.length >= maxColors) return;

        // Interpolate a new color in the middle
        const middleIndex = Math.floor(colors.length / 2);
        const newColor: UnityColor =
            colors.length > 0
                ? { r: 0.5, g: 0.5, b: 0.5, a: 1 }
                : { r: 1, g: 0, b: 0, a: 1 };

        const newColors = [...colors];
        newColors.splice(middleIndex, 0, newColor);
        onChange?.(newColors);
        setSelectedColorIndex(middleIndex);
    };

    const handleRemoveColor = (index: number) => {
        if (colors.length <= minColors) return;

        const newColors = colors.filter((_, i) => i !== index);
        onChange?.(newColors);

        if (selectedColorIndex === index) {
            // If removing the selected color, select the previous one or first one
            setSelectedColorIndex(Math.max(0, index - 1));
        } else if (selectedColorIndex > index) {
            setSelectedColorIndex(selectedColorIndex - 1);
        }
    };

    const handleColorChange = (index: number, color: UnityColor) => {
        const newColors = [...colors];
        newColors[index] = color;
        onChange?.(newColors);
    };

    const gradientCss = generateGradientCSS(colors);

    return (
        <div className={styles.container}>
            <div
                className={styles.gradientBar}
                style={{ background: gradientCss }}
            >
                {colors.map((color, index) => {
                    const position = getPosition(index);
                    return (
                        <div
                            key={index}
                            className={`${styles.stopMarker} ${
                                selectedColorIndex === index
                                    ? styles.selected
                                    : ''
                            }`}
                            style={{ left: `${position * 100}%` }}
                            onClick={() => setSelectedColorIndex(index)}
                        >
                            <div className={styles.stopLine} />
                            <div
                                className={styles.stopHandle}
                                style={{
                                    backgroundColor: unityColorToHex(color),
                                }}
                            />
                        </div>
                    );
                })}
            </div>

            <div className={styles.controls}>
                <Button
                    variant="contained"
                    onClick={handleAddColor}
                    disabled={colors.length >= maxColors}
                    className={styles.button}
                    title={
                        colors.length >= maxColors
                            ? `Maximum ${maxColors} colors`
                            : 'Add gradient color'
                    }
                >
                    + Add Color
                </Button>
                <span className={styles.stopCount}>
                    {colors.length} {colors.length === 1 ? 'color' : 'colors'} /{' '}
                    {maxColors}
                </span>
            </div>

            <div className={styles.selectedStopEditor}>
                <input
                    type="color"
                    value={unityColorToHex(colors[selectedColorIndex])}
                    onChange={(e) => {
                        const hexValue = e.target.value as HexColor;
                        const unityColor = hexToUnityColor(hexValue);
                        handleColorChange(selectedColorIndex, unityColor);
                    }}
                    className={styles.colorInput}
                    title="Color picker"
                />

                <div className={styles.editorActions}>
                    <IconButton
                        onClick={() => {
                            if (selectedColorIndex > 0) {
                                const newColors = [...colors];
                                const temp = newColors[selectedColorIndex];
                                newColors[selectedColorIndex] =
                                    newColors[selectedColorIndex - 1];
                                newColors[selectedColorIndex - 1] = temp;
                                onChange?.(newColors);
                                setSelectedColorIndex(selectedColorIndex - 1);
                            }
                        }}
                        disabled={selectedColorIndex === 0}
                        className={styles.arrowButton}
                        title="Move color left"
                    >
                        <Icon name="arrow_back" />
                    </IconButton>
                    <IconButton
                        onClick={() => handleRemoveColor(selectedColorIndex)}
                        disabled={colors.length <= minColors}
                        className={styles.removeButton}
                        title="Remove this color"
                    >
                        <Icon name="delete" />
                    </IconButton>
                    <IconButton
                        onClick={() => {
                            if (selectedColorIndex < colors.length - 1) {
                                const newColors = [...colors];
                                const temp = newColors[selectedColorIndex];
                                newColors[selectedColorIndex] =
                                    newColors[selectedColorIndex + 1];
                                newColors[selectedColorIndex + 1] = temp;
                                onChange?.(newColors);
                                setSelectedColorIndex(selectedColorIndex + 1);
                            }
                        }}
                        disabled={selectedColorIndex === colors.length - 1}
                        className={styles.arrowButton}
                        title="Move color right"
                    >
                        <Icon name="arrow_forward" />
                    </IconButton>
                </div>
            </div>
        </div>
    );
};

function getDefaultColors(count: number): UnityColor[] {
    const colors: UnityColor[] = [];
    for (let i = 0; i < count; i++) {
        const position = count === 1 ? 0.5 : i / (count - 1);
        colors.push({ r: position, g: 0.5, b: 1 - position, a: 1 });
    }
    return colors;
}

function generateGradientCSS(colors: UnityColor[]): string {
    if (colors.length === 0) return 'transparent';

    const colorStops = colors
        .map((color, index) => {
            const position =
                colors.length === 1 ? 50 : (index / (colors.length - 1)) * 100;
            return `${unityColorToHex(color)} ${position}%`;
        })
        .join(', ');
    return `linear-gradient(to right, ${colorStops})`;
}
